import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LocalizedLink } from "@/components/layout/LocalizedLink";
import { 
  ArrowRight, 
  Globe, 
  Shield, 
  Cpu, 
  Building2, 
  CheckCircle2,
  ChevronRight,
  Lock,
  Fingerprint,
  Users,
  Clock,
  FileCheck,
  Headphones,
  MapPin,
  BarChart3
} from "lucide-react";

const services = [
  {
    icon: Building2,
    titleKey: "services.visaTitle",
    descKey: "services.visaDesc",
    href: "/government-solutions",
  },
  {
    icon: Fingerprint,
    titleKey: "services.biometricTitle",
    descKey: "services.biometricDesc",
    href: "/technology-platform",
  },
  {
    icon: Globe,
    titleKey: "services.globalTitle",
    descKey: "services.globalDesc",
    href: "/operational-delivery",
  },
  {
    icon: Cpu,
    titleKey: "services.digitalTitle",
    descKey: "services.digitalDesc",
    href: "/technology-platform",
  },
];

const credentialPoints = [
  { icon: Clock, titleKey: "credentials.years", descKey: "credentials.yearsDesc" },
  { icon: FileCheck, titleKey: "credentials.cases", descKey: "credentials.casesDesc" },
  { icon: MapPin, titleKey: "credentials.countries", descKey: "credentials.countriesDesc" },
  { icon: Headphones, titleKey: "credentials.languages", descKey: "credentials.languagesDesc" },
];

const engagementModel = [
  { titleKey: "procurement.step1", descKey: "procurement.step1Desc" },
  { titleKey: "procurement.step2", descKey: "procurement.step2Desc" },
  { titleKey: "procurement.step3", descKey: "procurement.step3Desc" },
  { titleKey: "procurement.step4", descKey: "procurement.step4Desc" },
];

const slaExampleKeys = [
  { metricKey: "procurement.slaProcessing", rangeKey: "procurement.slaProcessingRange" },
  { metricKey: "procurement.slaBiometric", rangeKey: "procurement.slaBiometricRange" },
  { metricKey: "procurement.slaDocument", rangeKey: "procurement.slaDocumentRange" },
  { metricKey: "procurement.slaSupport", rangeKey: "procurement.slaSupportRange" },
];

export default function HomePage() {
  const { t } = useTranslation();

  // Dynamically preload hero background & Outfit font only on homepage
  useEffect(() => {
    const heroPreload = document.createElement("link");
    heroPreload.rel = "preload";
    heroPreload.href = "/images/hero-bg.webp";
    heroPreload.as = "image";
    heroPreload.type = "image/webp";
    heroPreload.setAttribute("fetchpriority", "high");

    const fontPreload = document.createElement("link");
    fontPreload.rel = "preload";
    fontPreload.href = "https://fonts.gstatic.com/s/outfit/v15/QGYvz_MVcBeNP4NJtEtqUYLknw.woff2";
    fontPreload.as = "font";
    fontPreload.type = "font/woff2";
    fontPreload.crossOrigin = "anonymous";

    document.head.appendChild(heroPreload);
    document.head.appendChild(fontPreload);

    return () => {
      heroPreload.remove();
      fontPreload.remove();
    };
  }, []);
  return (
    <div className="min-h-screen">
      {/* Hero Section - TRUE FULL SCREEN */}
      <section className="hero-top">
        {/* Premium Background - Using picture for WebP with JPG fallback */}
        <picture>
          <source srcSet="/images/hero-bg.webp" type="image/webp" />
          <img 
            src="/images/hero-bg.jpg"
            alt=""
            width={1920}
            height={1080}
            {...{ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>}
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/90 to-primary/80" />
        <div className="noise-texture" />
        <div className="grid-pattern" />

        <div className="container-premium relative z-10 flex flex-col justify-between flex-1 py-6 sm:py-8">
          {/* Main Content - Centered */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="max-w-3xl">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full hero-badge text-sm font-medium mb-4 sm:mb-6">
                <Shield className="h-4 w-4 text-accent" />
                <span>{t('hero.badge')}</span>
              </div>

              {/* Headline - Clear Typography */}
              <h1 className="hero-text mb-3 sm:mb-5 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight">
                {t('hero.title')}{" "}
                <span className="text-accent">{t('hero.titleHighlight')}</span>
              </h1>

              {/* Subheadline */}
              <p className="text-base sm:text-lg md:text-xl hero-text-muted max-w-2xl mb-6 sm:mb-8 leading-relaxed font-light">
                {t('hero.subtitle')}
              </p>

              {/* CTAs - Proper Hierarchy */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-4">
                <Button asChild className="cta-button-wrap h-12 sm:h-14 px-6 sm:px-8 rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground font-semibold text-base shadow-lg shadow-accent/25 justify-center">
                  <LocalizedLink to="/contact">
                    {t('common.discussPartnership')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </LocalizedLink>
                </Button>
                <Button asChild variant="outline" className="cta-button-wrap h-12 sm:h-14 px-6 sm:px-8 rounded-xl bg-primary-foreground/10 border-primary-foreground/25 hero-text hover:bg-primary-foreground/20 font-medium text-base backdrop-blur-sm justify-center">
                  <LocalizedLink to="/case-studies">
                    {t('common.viewCaseStudies')}
                  </LocalizedLink>
                </Button>
              </div>
            </div>
          </div>

          {/* Boundary Statement - Always visible at bottom of hero */}
          <div className="pt-4 sm:pt-6 hero-border border-t mt-6">
            <p className="hero-text-subtle text-xs sm:text-sm max-w-xl leading-relaxed">
              {t('hero.boundary')}
            </p>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-12 sm:py-14 md:py-16 bg-background border-b border-border">
        <div className="container-premium">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5 md:gap-6 lg:gap-8">
            {credentialPoints.map((point, index) => (
              <div key={index} className="text-center lg:text-left">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-accent/10 mb-4">
                  <point.icon className="h-5 w-5 text-accent" />
                </div>
                <p className="font-semibold text-foreground mb-1.5 text-base">
                  {t(point.titleKey)}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(point.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-background">
        <div className="container-premium">
          <div className="max-w-2xl mb-14">
            <div className="badge-premium mb-4">
              <Cpu className="h-3.5 w-3.5" />
              <span>{t('services.badge')}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-5 tracking-tight">
              {t('services.title')}
            </h2>
            <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
            {services.map((service) => (
              <LocalizedLink
                key={service.titleKey}
                to={service.href}
                className="card-interactive group p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                    <service.icon className="h-6 w-6 text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold mb-2 group-hover:text-accent transition-colors">
                      {t(service.titleKey)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(service.descKey)}
                    </p>
                    <div className="mt-4 inline-flex items-center text-accent text-sm font-medium">
                      {t('common.learnMore')}
                      <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </LocalizedLink>
            ))}
          </div>
        </div>
      </section>

      {/* Procurement-Ready Section */}
      <section className="section-padding bg-muted/40">
        <div className="container-premium">
          <div className="max-w-2xl mb-14">
            <div className="badge-premium mb-4">
              <FileCheck className="h-3.5 w-3.5" />
              <span>{t('procurement.badge')}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-5 tracking-tight">
              {t('procurement.title')}
            </h2>
            <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
              {t('procurement.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
            {/* Engagement Steps */}
            <div className="space-y-4">
              {engagementModel.map((step, index) => (
                <div key={index} className="flex gap-4 p-5 rounded-xl bg-card border border-border/60">
                  <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-accent font-bold text-sm">{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1.5">{t(step.titleKey)}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(step.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* SLA Examples & Governance */}
            <div className="space-y-5">
              <div className="p-6 rounded-xl bg-card border border-border/60">
                <div className="flex items-center gap-3 mb-5">
                  <BarChart3 className="h-5 w-5 text-accent" />
                  <p className="font-semibold">{t('procurement.slaTitle')}</p>
                </div>
                <div className="space-y-3">
                  {slaExampleKeys.map((sla, index) => (
                    <div key={index} className="flex justify-between items-center text-sm py-2.5 border-b border-border/40 last:border-0">
                      <span className="text-muted-foreground">{t(sla.metricKey)}</span>
                      <span className="font-medium text-foreground">{t(sla.rangeKey)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-5">
                  {t('procurement.slaNote')}
                </p>
              </div>

              <div className="p-6 rounded-xl bg-card border border-border/60">
                <div className="flex items-center gap-3 mb-5">
                  <Shield className="h-5 w-5 text-accent" />
                  <p className="font-semibold">{t('procurement.securityTitle')}</p>
                </div>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                    <span>{t('procurement.gdpr')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                    <span>{t('procurement.encryption')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                    <span>{t('procurement.audits')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                    <span>{t('procurement.certifications')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="section-padding bg-background">
        <div className="container-premium">
          <div className="grid md:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-start">
            <div>
              <div className="badge-premium mb-4">
                <Users className="h-3.5 w-3.5" />
                <span>{t('whyPartner.badge')}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-6 tracking-tight">
                {t('whyPartner.title')}
              </h2>
              <p className="text-muted-foreground text-base lg:text-lg leading-relaxed mb-8">
                {t('whyPartner.subtitle')}
              </p>

              <ul className="space-y-3 mb-10">
                {['point1', 'point2', 'point3', 'point4', 'point5', 'point6'].map((key) => (
                  <li key={key} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-3.5 w-3.5 text-accent" />
                    </div>
                    <span className="text-sm text-foreground">{t(`whyPartner.${key}`)}</span>
                  </li>
                ))}
              </ul>

              <Button asChild className="h-12 px-6 rounded-xl bg-primary hover:bg-primary/90 font-medium">
                <LocalizedLink to="/government-solutions">
                  {t('common.exploreSolutions')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </LocalizedLink>
              </Button>
            </div>

            {/* Cards */}
            <div className="space-y-4 md:space-y-5">
              <div className="card-elevated p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Lock className="h-5 w-5 text-accent" />
                  </div>
                  <h3 className="text-lg font-semibold">{t('whyPartner.securityTitle')}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('whyPartner.securityDesc')}
                </p>
              </div>

              <div className="card-elevated p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Globe className="h-5 w-5 text-accent" />
                  </div>
                  <h3 className="text-lg font-semibold">{t('whyPartner.globalTitle')}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('whyPartner.globalDesc')}
                </p>
              </div>

              <div className="card-elevated p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Headphones className="h-5 w-5 text-accent" />
                  </div>
                  <h3 className="text-lg font-semibold">{t('whyPartner.supportTitle')}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('whyPartner.supportDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-24 hero-section relative overflow-hidden">
        <div className="noise-texture" />
        <div className="grid-pattern" />
        
        <div className="container-premium relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold hero-text mb-6 tracking-tight">
              {t('cta.title')}
            </h2>
            <p className="hero-text-muted text-base sm:text-lg mb-10 leading-relaxed">
              {t('cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button asChild className="cta-button-wrap h-12 sm:h-14 px-8 rounded-xl bg-primary-foreground text-primary hover:bg-primary-foreground/95 font-semibold text-base shadow-lg">
                <LocalizedLink to="/contact">
                  {t('common.discussPartnership')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </LocalizedLink>
              </Button>
              <Button asChild variant="outline" className="cta-button-wrap h-12 sm:h-14 px-8 rounded-xl bg-transparent border-primary-foreground/30 hero-text hover:bg-primary-foreground/10 font-medium text-base">
                <LocalizedLink to="/resources">
                  {t('common.downloadCapabilities')}
                </LocalizedLink>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
